import { Box, Flex, Group, Stack, createStyles } from '@mantine/core';
import type { PolicyOut, PolicyRuleOut } from '@repo/api-codegen';
import { Icon, IconButton, Text, Title } from '@repo/foundations';
import { iconSize } from '@repo/theme/primitives';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useDeleteAutomation } from '../../api/hooks/automation';
import { openConfirmModal } from '../../components/ModalManager';
import { CreatePolicyStoreProvider } from './components/context';
import CreatePolicyButton from './components/CreatePolicyButton';
import PolicyRulesStack from './components/PolicyRulesStack';
import PropertiesStack from './components/PropertiesStack';
import RemediationStack from './components/RemediationStack';
import ResourceSelectionStack from './components/ResourceSelectionStack';
import { useCreatePolicyStore } from './components/useCreatePolicyStore';

const useStyles = createStyles((theme) => ({
	wrapper: {
		flexDirection: 'column',
		width: '100%',
		height: '100%',
		gap: 0,
		animation: 'fadeIn 0.35s ease-in-out',
	},
	header: {
		flexWrap: 'nowrap',
		justifyContent: 'space-between',
		padding: `${theme.spacing.sm} ${theme.spacing['3xl']}`,
	},
	content: {
		flexWrap: 'nowrap',
		justifyContent: 'space-between',
		height: 'calc(100vh - 52px)',
	},
	policyWrapper: {
		alignItems: 'flex-start',
		justifyContent: 'center',
		flexGrow: 1,
		overflowY: 'auto',
		padding: `${theme.spacing['4xl']} ${theme.spacing.xl}`,
		borderTopWidth: 1,
		borderStyle: 'solid',
		borderColor: theme.other.getColor('border/secondary/default'),
	},
	exitModalContent: {
		width: 100,
		borderRadius: theme.radius.lg,
	},
	policyIcon: {
		backgroundColor: theme.other.getColor('fill/secondary/default'),
		borderRadius: theme.radius.sm,
		padding: theme.spacing['3xs'],
	},
}));

type PoliciesCreatePageProps = {
	policy?: PolicyOut;
};

function PoliciesCreatePageContent({ policy }: PoliciesCreatePageProps) {
	const navigate = useNavigate();
	const location = useLocation();
	const store = useCreatePolicyStore();
	const { mutateAsync: deleteAutomation } = useDeleteAutomation({});

	const { classes, theme } = useStyles();
	const { hasUnsavedChanges } = store;
	const remediationRef = useRef<HTMLDivElement>(null);

	const exitPage = useCallback(() => {
		if (location.state && location.state['prev_url_path']) {
			navigate(location.state['prev_url_path']);
		} else {
			navigate('/policies/');
		}
	}, [location.state, navigate]);

	useEffect(() => {
		if (location.state?.scrollToBottom && remediationRef.current) {
			setTimeout(() => {
				remediationRef.current?.scrollIntoView({ behavior: 'smooth' });
			}, 150);
		}
	}, [location.state?.scrollToBottom]);

	const handleClose = useCallback(async () => {
		if (hasUnsavedChanges) {
			openConfirmModal({
				title: <Title size="md">Leave page with unsaved changes?</Title>,
				children: 'All progress will be lost.',
				labels: { confirm: 'Leave page', cancel: 'Cancel' },
				cancelProps: { size: 'md' },
				confirmProps: { variant: 'primary', tone: 'default', size: 'md' },
				groupProps: {
					spacing: 'xs',
				},
				onConfirm: async () => {
					await store.cleanupOrphanedAutomation(deleteAutomation);
					exitPage();
				},
				classNames: {
					content: classes.exitModalContent,
				},
			});
		} else {
			exitPage();
		}
	}, [
		exitPage,
		classes.exitModalContent,
		hasUnsavedChanges,
		store,
		deleteAutomation,
	]);

	return (
		<Flex className={classes.wrapper}>
			<Flex className={classes.header}>
				<div
					style={{
						width: iconSize.md,
						height: iconSize.md,
						visibility: 'hidden',
					}}
				/>
				<Group>
					<Box className={classes.policyIcon}>
						<Icon name="fileCheck" />
					</Box>
					<Text size="sm" weight="bold">
						{policy ? 'Edit policy' : 'New policy'}
					</Text>
				</Group>
				<IconButton variant="tertiary" iconName="x" onClick={handleClose} />
			</Flex>
			<Flex className={classes.content}>
				<Flex className={classes.policyWrapper}>
					<Stack w={theme.other.width.lg} spacing="4xl">
						<PropertiesStack />
						<ResourceSelectionStack />
						<PolicyRulesStack policyId={policy?.id} />
						<div ref={remediationRef}>
							<RemediationStack />
						</div>
						<CreatePolicyButton exitPage={exitPage} policyId={policy?.id} />
					</Stack>
				</Flex>
			</Flex>
		</Flex>
	);
}

const PoliciesCreatePageContentObserved = observer(PoliciesCreatePageContent);

function PoliciesCreatePage({
	policy,
	policyRules,
}: PoliciesCreatePageProps & {
	policyRules: PolicyRuleOut[];
}) {
	const location = useLocation();
	const policyTemplate = location.state?.policyTemplate;
	const owners = location.state?.owners;
	const subscribers = location.state?.subscribers;
	const piiScanMetadata = location.state?.piiScanMetadata;

	return (
		<CreatePolicyStoreProvider
			policy={policy}
			policyTemplate={policyTemplate}
			policyRules={policyRules}
			owners={owners}
			subscribers={subscribers}
			piiScanMetadata={piiScanMetadata}
		>
			<PoliciesCreatePageContentObserved policy={policy} />
		</CreatePolicyStoreProvider>
	);
}

export default PoliciesCreatePage;
